import { CheckBox } from '@toggle/design-system';
import React, { ComponentProps, ReactNode } from 'react';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';

import * as S from './Filter.styles';

type FilterCheckBox = React.ComponentType<ComponentProps<typeof CheckBox>>;

export interface OptionProps {
  option: FilterOption;
  disabled?: boolean;
  multiselect?: boolean;
  isChecked?: boolean;
  disabledTooltipLabel?: string;
  handleClick: () => void;
  direction?: S.ItemDirection;
  indentedDescription?: number;
  renderCustomFilterOptionLabel?: (filterOption: FilterOption) => ReactNode;
  dataTestId?: string;
  'data-testid'?: string;
}

export const Option = ({
  option,
  disabled,
  multiselect,
  isChecked,
  disabledTooltipLabel,
  handleClick,
  direction = 'column',
  indentedDescription = 0,
  renderCustomFilterOptionLabel,
  dataTestId,
  'data-testid': dataTestIdAttr,
}: OptionProps) => {
  const TypedCheckBox = CheckBox as FilterCheckBox;
  const resolvedTestId =
    dataTestIdAttr ??
    dataTestId ??
    (multiselect ? 'multiselect-item' : 'single-item');

  if (multiselect) {
    return (
      <TooltipContainer label={disabledTooltipLabel}>
        <S.CheckboxItem
          onClick={e => {
            e.preventDefault();

            if (!disabled && !disabledTooltipLabel) {
              handleClick();
            }
          }}
          disabled={!!disabledTooltipLabel}
        >
          <S.ItemText data-testid={resolvedTestId}>
            <TypedCheckBox
              label={
                <S.ItemLabel $locked={disabled}>
                  {renderCustomFilterOptionLabel?.(option) ?? option.name}
                </S.ItemLabel>
              }
              name={option.key}
              checked={isChecked}
              variant={disabled ? 'disabled' : 'primary'}
            />
          </S.ItemText>
        </S.CheckboxItem>
      </TooltipContainer>
    );
  }

  return (
    <S.Item
      onSelect={e => {
        if (option.childName) {
          e.preventDefault();
        }

        handleClick();
      }}
      $isChecked={isChecked}
      disabled={disabled}
    >
      <S.MenuItemLeftIndicator />
      <S.ItemText $direction={direction} data-testid={resolvedTestId}>
        <S.ItemLabel $locked={disabled}>
          {renderCustomFilterOptionLabel?.(option) ?? option.name}
        </S.ItemLabel>
        <S.ItemDescription $indented={indentedDescription}>
          {option.description}
        </S.ItemDescription>
      </S.ItemText>
    </S.Item>
  );
};
